import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, ShieldCheck, CheckCircle2, AlertCircle, Lock } from "lucide-react";
import PasswordInput from "../components/PasswordInput";
import PasswordStrengthMeter, { evaluatePassword } from "../components/PasswordStrengthMeter";
import Logo from "../components/Logo";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function fmtErr(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

/**
 * Admin self-serve password reset.
 *
 * URL: /admin/reset-password?token=...
 * - If token missing → renders the "Forgot password" form (request email)
 * - If token present → renders the "Set new password" form
 */
export default function AdminResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  return token ? <SetNewPassword token={token} /> : <RequestReset />;
}

function RequestReset() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/api/auth/admin/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail, "Có lỗi xảy ra. Vui lòng thử lại."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <Hero
        badge="Khôi phục tài khoản"
        title="Quên mật khẩu?"
        subtitle="Nhập email Quản trị. Chúng tôi sẽ gửi đường liên kết đặt lại mật khẩu (hết hạn sau 30 phút)."
      />
      {sent ? (
        <div className="bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(14,91,79,0.25)] border border-white/60 p-6 sm:p-8 text-center" data-testid="forgot-success">
          <CheckCircle2 className="w-12 h-12 text-emerald2 mx-auto mb-3" />
          <h2 className="font-serif text-2xl text-charcoal">Đã gửi liên kết!</h2>
          <p className="mt-2 text-sm text-warmgray-700">
            Nếu email khớp với tài khoản Quản trị, bạn sẽ nhận được email trong vài phút tới. Kiểm tra cả hộp thư rác.
          </p>
          <Link to="/admin/login" className="inline-flex items-center gap-1.5 mt-5 text-sm font-medium text-emerald2 hover:underline" data-testid="back-to-login-after-sent">
            <ArrowLeft className="w-4 h-4" /> Quay lại đăng nhập
          </Link>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(14,91,79,0.25)] border border-white/60 p-6 sm:p-8 space-y-4"
          data-testid="admin-forgot-form"
        >
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">Email Quản trị</span>
            <input
              type="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admins@nailvietclub.com"
              data-testid="admin-forgot-email"
              className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40"
            />
          </label>
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm flex items-center gap-2" data-testid="admin-forgot-error">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          <button
            type="submit"
            disabled={!email.trim() || busy}
            data-testid="admin-forgot-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-charcoal text-white rounded-2xl py-3 text-sm font-medium hover:bg-charcoal-900 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Gửi liên kết đặt lại
          </button>
          <div className="text-center pt-2">
            <Link to="/admin/login" className="text-xs text-warmgray-700 hover:text-charcoal" data-testid="back-to-login">
              <ArrowLeft className="inline w-3 h-3 mr-1" /> Quay lại đăng nhập
            </Link>
          </div>
        </form>
      )}
    </Page>
  );
}

function SetNewPassword({ token }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const strength = evaluatePassword(pw);
  const canSubmit = pw.length >= 8 && pw === pw2 && strength.score >= 2 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/api/auth/admin/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/admin/login", { replace: true }), 2000);
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail, "Liên kết không hợp lệ hoặc đã hết hạn."));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Page>
        <div className="bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(14,91,79,0.25)] border border-white/60 p-6 sm:p-8 text-center" data-testid="reset-success">
          <CheckCircle2 className="w-12 h-12 text-emerald2 mx-auto mb-3" />
          <h2 className="font-serif text-2xl text-charcoal">Mật khẩu đã được cập nhật!</h2>
          <p className="mt-2 text-sm text-warmgray-700">Đang chuyển bạn về trang đăng nhập…</p>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <Hero
        badge="Đặt lại mật khẩu"
        title="Chọn mật khẩu mới"
        subtitle="Mật khẩu mạnh: ít nhất 8 ký tự, kết hợp chữ hoa, chữ thường, số và ký hiệu."
      />
      <form
        onSubmit={submit}
        className="bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(14,91,79,0.25)] border border-white/60 p-6 sm:p-8 space-y-4"
        data-testid="admin-reset-form"
      >
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">Mật khẩu mới</span>
          <PasswordInput
            autoFocus
            required
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            minLength={8}
            autoComplete="new-password"
            placeholder="Tối thiểu 8 ký tự"
            data-testid="admin-reset-new-password"
            testIdSuffix="admin-reset-new"
            leftIcon={<Lock className="w-4 h-4 text-warmgray-500" />}
            className="w-full bg-white border border-warmgray-200 rounded-xl py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40"
          />
          <PasswordStrengthMeter password={pw} testIdPrefix="admin-reset-strength" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">Nhập lại mật khẩu</span>
          <PasswordInput
            required
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            minLength={8}
            autoComplete="new-password"
            placeholder="Nhập lại mật khẩu mới"
            data-testid="admin-reset-confirm"
            testIdSuffix="admin-reset-confirm"
            leftIcon={<Lock className="w-4 h-4 text-warmgray-500" />}
            className="w-full bg-white border border-warmgray-200 rounded-xl py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40"
          />
          {pw2.length > 0 && pw !== pw2 && (
            <span className="block mt-1 text-xs text-red-700" data-testid="admin-reset-mismatch">Mật khẩu không khớp.</span>
          )}
        </label>
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm flex items-center gap-2" data-testid="admin-reset-error">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="admin-reset-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-charcoal text-white rounded-2xl py-3 text-sm font-medium hover:bg-charcoal-900 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Cập nhật mật khẩu
        </button>
      </form>
    </Page>
  );
}

function Page({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-linen relative overflow-hidden" data-testid="admin-reset-page">
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #0E5B4F 0%, transparent 40%), radial-gradient(circle at 80% 80%, #C9A14A 0%, transparent 40%)" }} />
      <header className="relative z-10 px-5 md:px-10 py-5 flex items-center justify-between">
        <Link to="/admin/login" className="inline-flex items-center gap-2 text-espresso-muted hover:text-espresso text-sm">
          <ArrowLeft className="w-4 h-4" /> Đăng nhập
        </Link>
        <Logo dark={false} variant="full" to="/" />
      </header>
      <main className="relative z-10 flex-1 flex items-center justify-center px-5 sm:px-6 py-6 sm:py-10">
        <div className="w-full max-w-md">{children}</div>
      </main>
    </div>
  );
}

function Hero({ badge, title, subtitle }) {
  return (
    <div className="text-center mb-6 sm:mb-8">
      <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-espresso/10 rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.28em] font-semibold text-espresso">
        <ShieldCheck className="w-3.5 h-3.5 text-gold" /> {badge}
      </div>
      <h1 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl text-espresso leading-tight">{title}</h1>
      <p className="mt-2 text-sm text-warmgray-700">{subtitle}</p>
    </div>
  );
}
